import {
  ClockCircleOutlined,
  EnvironmentOutlined,
  InboxOutlined,
} from '@ant-design/icons'
import { Tooltip, Typography, theme } from 'antd'
import { useCallback, useEffect, useRef, useState } from 'react'
import { GRADIENT_BLEND, ZONE_COLORS } from './constants'
import type { ChangeCategory, UntilStage } from './types'

const { Text } = Typography

// Horizontal inset (px) on each side of the track so the first/last tick labels can
// stay centered under their ticks without spilling past the modal edge.
const TRACK_EDGE_PADDING = 44

const CATEGORY_ICONS: Record<ChangeCategory, React.ReactNode> = {
  destination: <EnvironmentOutlined />,
  supply: <InboxOutlined />,
  delivery_window: <ClockCircleOutlined />,
}

interface DragHandleProps {
  position: string
  isDragging: boolean
  onMouseDown: (e: React.MouseEvent) => void
  tooltipTitle: string
  tooltipOpen: boolean
  disabled?: boolean
}

function DragHandle({
  position,
  isDragging,
  onMouseDown,
  tooltipTitle,
  tooltipOpen,
  disabled,
}: DragHandleProps) {
  const gripColor = disabled ? '#d1d5db' : '#9ca3af'
  return (
    <Tooltip title={tooltipTitle} open={disabled ? undefined : tooltipOpen}>
      <div
        onMouseDown={disabled ? undefined : onMouseDown}
        style={{
          position: 'absolute',
          left: position,
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 14,
          height: 40,
          backgroundColor: disabled ? '#e5e7eb' : '#ffffff',
          border: '1px solid #d1d5db',
          borderRadius: 4,
          cursor: disabled ? 'not-allowed' : isDragging ? 'grabbing' : 'grab',
          opacity: disabled ? 0.6 : 1,
          zIndex: isDragging ? 12 : 10,
          boxShadow: disabled ? 'none' : '0 2px 4px rgba(0, 0, 0, 0.15)',
          transition: isDragging ? 'none' : 'left 0.15s ease',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
        }}
      >
        <div style={{ width: 6, height: 1, backgroundColor: gripColor }} />
        <div style={{ width: 6, height: 1, backgroundColor: gripColor }} />
        <div style={{ width: 6, height: 1, backgroundColor: gripColor }} />
      </div>
    </Tooltip>
  )
}

interface ChangeRuleSliderProps {
  category: ChangeCategory
  label: string
  masterStages: string[] // All possible stages for alignment
  validStages: string[] // Stages valid for this change type
  autoAcceptUntil: UntilStage
  manualApprovalUntil: UntilStage
  onAutoAcceptChange: (stage: UntilStage) => void
  onManualApprovalChange: (stage: UntilStage) => void
  // When true the auto-accept handle is locked and cannot be dragged. Used when the
  // connected local instance is an external (non-S&D) system, where order changes can
  // never be auto-accepted.
  autoAcceptDisabled?: boolean
}

export function ChangeRuleSlider({
  category,
  label,
  masterStages,
  validStages,
  autoAcceptUntil,
  manualApprovalUntil,
  onAutoAcceptChange,
  onManualApprovalChange,
  autoAcceptDisabled = false,
}: ChangeRuleSliderProps) {
  const { token } = theme.useToken()
  const trackRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'auto' | 'manual' | null>(null)

  // Use masterStages for positioning (alignment) but validStages for interaction
  const autoAcceptIndex = masterStages.indexOf(autoAcceptUntil as never)
  const manualApprovalIndex = masterStages.indexOf(manualApprovalUntil as never)
  const lastValidIndex = masterStages.indexOf(
    validStages[validStages.length - 1],
  )

  const getPositionPercent = (index: number) =>
    (index / (masterStages.length - 1)) * 100

  const autoAcceptPercent = getPositionPercent(autoAcceptIndex)
  const manualApprovalPercent = getPositionPercent(manualApprovalIndex)
  const lastValidPercent = getPositionPercent(lastValidIndex)

  const getStageFromPosition = useCallback(
    (clientX: number): UntilStage | null => {
      if (!trackRef.current) return null
      const rect = trackRef.current.getBoundingClientRect()
      const x = clientX - rect.left
      const percent = (x / rect.width) * 100

      let nearestIndex = 0
      let minDistance = Number.POSITIVE_INFINITY

      // Only snap to valid stages
      for (let i = 0; i < masterStages.length; i++) {
        if (!validStages.includes(masterStages[i])) continue
        const stagePercent = getPositionPercent(i)
        const distance = Math.abs(percent - stagePercent)
        if (distance < minDistance) {
          minDistance = distance
          nearestIndex = i
        }
      }

      return masterStages[nearestIndex] as UntilStage
    },
    [masterStages, validStages],
  )

  const handleMouseDown = useCallback(
    (handle: 'auto' | 'manual') => (e: React.MouseEvent) => {
      if (handle === 'auto' && autoAcceptDisabled) return
      e.preventDefault()
      setDragging(handle)
    },
    [autoAcceptDisabled],
  )

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging) return

      const stage = getStageFromPosition(e.clientX)
      if (!stage) return

      const stageIndex = masterStages.indexOf(stage as never)

      if (dragging === 'auto') {
        if (stageIndex <= manualApprovalIndex) {
          onAutoAcceptChange(stage)
        }
      } else {
        if (stageIndex >= autoAcceptIndex) {
          onManualApprovalChange(stage)
        }
      }
    },
    [
      dragging,
      getStageFromPosition,
      masterStages,
      autoAcceptIndex,
      manualApprovalIndex,
      onAutoAcceptChange,
      onManualApprovalChange,
    ],
  )

  const handleMouseUp = useCallback(() => {
    setDragging(null)
  }, [])

  useEffect(() => {
    if (dragging) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      return () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
      }
    }
    return undefined
  }, [dragging, handleMouseMove, handleMouseUp])

  const samePosition = autoAcceptIndex === manualApprovalIndex

  return (
    <div style={{ marginBottom: 24 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <span style={{ fontSize: 16 }}>{CATEGORY_ICONS[category]}</span>
        <Text strong>{label}</Text>
      </div>

      {/* Inset the track/ticks from the edges so the first and last labels can stay
          centered under their ticks without overflowing the modal. */}
      <div style={{ padding: `0 ${TRACK_EDGE_PADDING}px` }}>
        <div
          ref={trackRef}
          style={{
            position: 'relative',
            height: 32,
            cursor: dragging ? 'grabbing' : 'default',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: `${lastValidPercent}%`,
              height: '100%',
              borderRadius: lastValidPercent < 100 ? '4px 0 0 4px' : 4,
              overflow: 'hidden',
              zIndex: 1,
              background: samePosition
                ? `linear-gradient(to right,
                  ${ZONE_COLORS.autoAccept} 0%,
                  ${ZONE_COLORS.autoAccept} ${Math.max(0, (autoAcceptPercent / lastValidPercent) * 100 - GRADIENT_BLEND)}%,
                  ${ZONE_COLORS.autoReject} ${Math.min(100, (autoAcceptPercent / lastValidPercent) * 100 + GRADIENT_BLEND)}%,
                  ${ZONE_COLORS.autoReject} 100%)`
                : `linear-gradient(to right,
                  ${ZONE_COLORS.autoAccept} 0%,
                  ${ZONE_COLORS.autoAccept} ${Math.max(0, (autoAcceptPercent / lastValidPercent) * 100 - GRADIENT_BLEND)}%,
                  ${ZONE_COLORS.manual} ${Math.min(100, (autoAcceptPercent / lastValidPercent) * 100 + GRADIENT_BLEND)}%,
                  ${ZONE_COLORS.manual} ${Math.max(0, (manualApprovalPercent / lastValidPercent) * 100 - GRADIENT_BLEND)}%,
                  ${ZONE_COLORS.autoReject} ${Math.min(100, (manualApprovalPercent / lastValidPercent) * 100 + GRADIENT_BLEND)}%,
                  ${ZONE_COLORS.autoReject} 100%)`,
              transition: dragging ? 'none' : 'background 0.15s ease',
            }}
          />
          {/* Inactive zone for stages beyond this change type's limit */}
          {lastValidPercent < 100 && (
            <div
              style={{
                position: 'absolute',
                left: `${lastValidPercent}%`,
                top: '50%',
                transform: 'translateY(-50%)',
                right: 0,
                height: 4,
                borderRadius: '0 2px 2px 0',
                backgroundColor: token.colorBgContainerDisabled,
                zIndex: 0,
              }}
            />
          )}

          <DragHandle
            position={`calc(${autoAcceptPercent}%${samePosition ? ' - 10px' : ''})`}
            isDragging={dragging === 'auto'}
            onMouseDown={handleMouseDown('auto')}
            tooltipTitle={
              autoAcceptDisabled
                ? 'Auto-accept is unavailable'
                : `Auto-accept until: ${autoAcceptUntil}`
            }
            tooltipOpen={dragging === 'auto'}
            disabled={autoAcceptDisabled}
          />

          <DragHandle
            position={`calc(${manualApprovalPercent}%${samePosition ? ' + 10px' : ''})`}
            isDragging={dragging === 'manual'}
            onMouseDown={handleMouseDown('manual')}
            tooltipTitle={`Manual approval until: ${manualApprovalUntil}`}
            tooltipOpen={dragging === 'manual'}
          />
        </div>

        <div style={{ position: 'relative', height: 24, marginTop: 4 }}>
          {masterStages.map((stage, index) => {
            const percent = getPositionPercent(index)
            const isValid = validStages.includes(stage)
            return (
              <div
                key={stage}
                style={{
                  position: 'absolute',
                  left: `${percent}%`,
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  opacity: isValid ? 1 : 0.35,
                }}
              >
                <div
                  style={{
                    width: 1,
                    height: 6,
                    backgroundColor: token.colorTextTertiary,
                  }}
                />
                <Text
                  style={{
                    fontSize: 10,
                    color: token.colorTextSecondary,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {stage}
                </Text>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
